import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react'
import Modal from '../Modal/Modal'

/**
 * Confirmação de exclusão de grupo.
 *
 * Grupo vazio: confirmação simples. Grupo com conteúdo: avisa exatamente para onde
 * os itens vão (o servidor sobe filhos e devices um nível, para o grupo pai ou para
 * a lista principal) e só libera o botão depois do check — apagar não pode ser um
 * clique distraído que espalha device pela lista sem o usuário perceber.
 */
export default function DeleteGroupModal({
  isOpen,
  onClose,
  onConfirm,
  group,
  deviceCount = 0,
  subGroupCount = 0,
  destinationName,
  loading = false,
  countsLoading = false,
}) {
  const { t } = useTranslation()
  const [acknowledged, setAcknowledged] = useState(false)

  useEffect(() => {
    if (isOpen) setAcknowledged(false)
  }, [isOpen, group?.id])

  if (!group) return null

  const isEmpty = deviceCount === 0 && subGroupCount === 0
  // Enquanto as contagens nao chegam o grupo "parece" vazio; confirmar aqui
  // pularia o aviso, entao o botao fica travado ate o servidor responder.
  const canDelete = !countsLoading && (isEmpty || acknowledged)

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('groups.deleteModal.title', { name: group.name })}>
      <div className="space-y-4">
        {countsLoading ? (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('groups.deleteModal.loadingCounts')}
          </p>
        ) : isEmpty ? (
          <p className="text-sm text-gray-600">{t('groups.deleteModal.emptyHint')}</p>
        ) : (
          <>
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden />
              <div className="space-y-1 text-sm text-amber-800">
                <p className="font-medium">{t('groups.deleteModal.notEmptyTitle')}</p>
                <ul className="list-disc space-y-0.5 pl-4">
                  {deviceCount > 0 && <li>{t('groups.deleteModal.deviceCount', { count: deviceCount })}</li>}
                  {subGroupCount > 0 && <li>{t('groups.deleteModal.subGroupCount', { count: subGroupCount })}</li>}
                </ul>
                <p>{t('groups.deleteModal.movesTo', { destination: destinationName })}</p>
              </div>
            </div>

            <label className="flex cursor-pointer select-none items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span>{t('groups.deleteModal.acknowledge', { destination: destinationName })}</span>
            </label>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>
            {t('common.buttons.cancel')}
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onConfirm}
            disabled={!canDelete || loading}
          >
            <Trash2 className="h-4 w-4" />
            {loading ? t('groups.deleteModal.deleting') : t('groups.deleteModal.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
